import { useEffect, useState } from 'react';

import { Dropdown } from 'components/UI/Dropdown';
import { Icon } from 'components/UI/Icons';
import { Svgs } from 'environment';
import { useLanguage } from 'features/hooks/useLanguage';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ProjectStatus } from 'types/data/projects/constants';
import { LanguageType } from 'types/enums';

interface Props {
	onDropdownVisible: (visible: boolean) => void;
	onTransferProject: () => void;
	onFreezeProject: () => void;
	onUnfreezeProject: () => void;
	onViewCollaboratorsClicked: () => void;
	showTransferOwnership: boolean;
	projectName: string;
	projectStatus: ProjectStatus;
}

export function ProjectSettingsDropdown({
	onTransferProject,
	onDropdownVisible,
	onViewCollaboratorsClicked,
	onFreezeProject,
	onUnfreezeProject,
	showTransferOwnership,
	projectName,
	projectStatus
}: Props) {
	const { enableProjectFreezing } = useFlags();

	const language = useLanguage();
	const dictionary = DICTIONARY[language];

	const [dropdownVisible, setDropdownVisible] = useState(false);

	const { enterpriseViewCollaboratorsFlag } = useFlags();

	useEffect(() => {
		onDropdownVisible(dropdownVisible);
	}, [dropdownVisible]);

	if (!showTransferOwnership && !enterpriseViewCollaboratorsFlag) {
		return null;
	}

	return (
		<Dropdown
			onClose={() => setDropdownVisible(false)}
			onOpen={() => setDropdownVisible(true)}
			toggleComponent={({ ref, toggle }) => (
				<Icon
					ref={ref}
					variant={v => v.button}
					svg={Svgs.More}
					size={s => s.m}
					onClick={toggle}
					active={dropdownVisible}
					dataTestId={'project-settings-dropdown-toggle_' + projectName}
				/>
			)}
			width={18}
			offset={{ top: 10, right: 4 }}
		>
			{showTransferOwnership && (
				<Dropdown.Item title={dictionary.transferOwnership} onClick={onTransferProject} />
			)}

			{enterpriseViewCollaboratorsFlag && (
				<Dropdown.Item
					title={dictionary.viewCollaborators}
					onClick={onViewCollaboratorsClicked}
				/>
			)}

			{enableProjectFreezing && projectStatus === ProjectStatus.Ongoing && (
				<Dropdown.Item title={dictionary.freezeProject} onClick={onFreezeProject} />
			)}

			{enableProjectFreezing && projectStatus === ProjectStatus.Frozen && (
				<Dropdown.Item title={dictionary.unfreezeProject} onClick={onUnfreezeProject} />
			)}
		</Dropdown>
	);
}

const DICTIONARY = {
	[LanguageType.English]: {
		freezeProject: 'Freeze Project',
		unfreezeProject: 'Unfreeze Project',
		transferOwnership: 'Transfer Ownership',
		viewCollaborators: 'View Collaborators'
	},
	[LanguageType.Norwegian]: {
		freezeProject: 'Frost Project',
		unfreezeProject: 'Unfrost Project',
		transferOwnership: 'Overta eierskap',
		viewCollaborators: 'Se deltakere'
	}
};
